import {getCurrentUserLogin} from '@github-ui/client-env'

import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'
import {useReviewers} from '../../data/use-reviewers'
import {PullRequestState} from '../../../../types'
import type {PendingReviewRequest} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import type {Reviewer} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'

export type ReviewRequestedKind = 'direct' | 'codeowner' | 'delegated'

export type ReviewRequestedData = {
  kind: ReviewRequestedKind
  /** Teams the review was delegated from (only for the `delegated` kind). */
  delegatedFromTeams: Reviewer[]
}

function isPendingRequestForViewer(request: PendingReviewRequest, viewerLogin: string): boolean {
  const reviewer = request.reviewer
  return reviewer?.reviewerType === 'User' && reviewer.displayLogin === viewerLogin
}

/**
 * Pure classification of the viewer's requested-review state. Returns `null` when the
 * viewer is not a requested reviewer. Codeowner and delegation add context to a direct
 * request.
 */
export function selectReviewRequested(
  pendingReviewRequests: PendingReviewRequest[],
  viewerLogin: string,
): ReviewRequestedData | null {
  const request = pendingReviewRequests.find(pending => isPendingRequestForViewer(pending, viewerLogin))
  if (!request) return null

  if (request.isCodeOwnerFromDelegation && request.delegatedFromTeam) {
    return {kind: 'delegated', delegatedFromTeams: [request.delegatedFromTeam]}
  }

  if (request.isCodeOwner) {
    return {kind: 'codeowner', delegatedFromTeams: []}
  }

  return {kind: 'direct', delegatedFromTeams: []}
}

/**
 * Surfaces the viewer's requested-review state for the "Needs attention" section, or
 * `null` when there is nothing to show. Only open, non-queued pull requests qualify.
 */
export function useReviewRequestedData(): ReviewRequestedData | null {
  const {pullRequest} = useLayoutRouteData()
  const {data: reviewers} = useReviewers()
  const viewerLogin = getCurrentUserLogin()

  if (!viewerLogin) return null
  if (pullRequest.state !== PullRequestState.Open) return null

  return selectReviewRequested(reviewers.pendingReviewRequests ?? [], viewerLogin)
}
